/* Each divider reveals its labels and releases its shutters at the CTA.
   The first divider waits for the grid entrance and carousel rise. */
(() => {
  const desktop = matchMedia('(min-width:1201px)');
  if (!document.body.classList.contains('ak-papers-editorial-page')) return;
  const pairs = [];
  let pending = 0;
  const reducedMotion = matchMedia('(prefers-reduced-motion:reduce)');
  // Opening construction is an overlay, never a spacer before the header.
  function introHeight() { return 0; }
  function settleRevisit(pair) {
    if (!pair.divider.hasAttribute('data-ak-paper-intro')) return;
    (pair.introAnimations || []).forEach(animation => animation.cancel());
    pair.divider.querySelectorAll('.ak-vpcompress22-desktop__line').forEach(line => {
      line.style.transform = 'scaleY(1)'; line.dataset.akPaperLineDrawn = 'true';
    });
    pair.divider.dataset.akPaperIntro = 'done';
    pair.introAnimating = false;
    pair.introSettled = true;
    reveal(pair, 'title');
    reveal(pair, 'cta');
  }
  async function startIntro(pair) {
    if (pair.introStarted || !desktop.matches) return;
    pair.introStarted = true;
    // Astrobear's seven-layer hero owns its desktop entrance and carousel rise.
    // Keep the shared reveal events, but do not run a second construction intro.
    const astro = document.querySelector('[data-ak-astro][data-astro-desktop]');
    if (astro && document.body.classList.contains('ak-astrobear-front-page')) {
      pair.introAnimating = true;
      const release = () => { settleRevisit(pair); sync(); };
      if (['assembled','rising','ready','fallback'].includes(astro.dataset.astroDesktop)) release();
      else window.addEventListener('ak:astro-desktop-layers-ready', release, {once:true});
      return;
    }
    const divider = pair.divider;
    divider.dataset.akPaperIntro = 'draw';
    pair.introAnimating = true;
    const lines = [...divider.querySelectorAll('.ak-vpcompress22-desktop__line')];
    const animations = lines.map(line => line.animate([
      {transform:'scaleY(0)'}, {transform:'scaleY(1)'}
    ], {duration: reducedMotion.matches ? 1 : 700,
      easing:'cubic-bezier(.45,0,.25,1)', fill:'forwards'}));
    pair.introAnimations = animations;
    await Promise.all(animations.map(animation => animation.finished.catch(() => {})));
    if (divider.dataset.akPaperIntro === 'done' || !desktop.matches) return;
    lines.forEach(line => { line.style.transform = 'scaleY(1)'; line.dataset.akPaperLineDrawn = 'true'; });
    animations.forEach(animation => animation.cancel());
    divider.dataset.akPaperIntro = 'rise';
    reveal(pair, 'title');
    reveal(pair, 'cta');
    const distance = Math.max(0, innerHeight - divider.getBoundingClientRect().top);
    const rise = [];
    for (let owner = divider.closest('.shopify-section').nextElementSibling; owner; owner = owner.nextElementSibling) {
      rise.push(owner.animate([{translate:`0 ${distance}px`}, {translate:'0 0'}], {
        duration:reducedMotion.matches ? 1 : 1800,
        easing:'cubic-bezier(.22,1,.36,1)', fill:'forwards'
      }));
    }
    pair.introAnimations = rise;
    await Promise.all(rise.map(animation => animation.finished.catch(() => {})));
    settleRevisit(pair);
    sync();
  }
  function reveal(pair, part) {
    const state = `ak-paper-${part}-ready`;
    if (pair.carousel.classList.contains(state)) return;
    if (part === 'cta') {
      const controls = pair.carousel.querySelector('[data-ak-catalog-controls]');
      if (controls) {
        controls.classList.add('ak-catalog-entering');
        setTimeout(() => controls.classList.remove('ak-catalog-entering'), reducedMotion.matches ? 0 : 700);
      }
    }
    pair.carousel.classList.add(state);
    if (part === 'cta' && pair.introSettled && pair.divider.hasAttribute('data-ak-paper-intro')) {
      pair.divider.dataset.akPaperIntro = 'done';
    }
    if (part === 'cta') window.dispatchEvent(new CustomEvent('ak:papers-editorial-cta-ready', {
      detail: {section: pair.carousel}
    }));
  }
  function setValue(element, name, value) {
    if (element.style.getPropertyValue(name) !== value) element.style.setProperty(name, value);
  }
  function layoutPosition(element, root) {
    let left = 0, top = 0;
    for (let node = element; node && node !== root; node = node.offsetParent) {
      left += node.offsetLeft;
      top += node.offsetTop;
    }
    return {left, top};
  }
  function sync() {
    pending = 0;
    if (!desktop.matches || pairs.some(pair => pair.introAnimating)) return;
    pairs.filter(pair => pair.introSettled).forEach(pair => {
      setValue(pair.divider, '--ak-paper-intro-height', `${introHeight(pair)}px`);
    });
    const reads = pairs.map(pair => {
      if (pair.divider.hasAttribute('data-ak-paper-intro')) return {pair, opening:true};
      const source = pair.divider.getBoundingClientRect();
      const grid = pair.divider.querySelector('.ak-vpcompress22-desktop__grid').getBoundingClientRect();
      const title = pair.carousel.querySelector('.ak-carousel-editorial-title').getBoundingClientRect();
      const cta = pair.carousel.querySelector('.ak-carousel-explore-slot, .ak-carousel-editorial-explore').getBoundingClientRect();
      const main = pair.pillars?.parentElement;
      const origin = main ? layoutPosition(pair.divider, main) : null;
      const carousel = pair.pillars ? pair.carousel.getBoundingClientRect() : null;
      return {pair, pillarBox: main ? {
        left: origin.left, top: origin.top,
        width: source.width, height: Math.max(innerHeight, carousel.bottom - source.top)
      } : null, base: Math.max(1, source.bottom - grid.bottom),
        hatchPhase: Math.max(0, title.top - source.top - 2),
        title: Math.max(0, title.top - source.bottom),
        cta: Math.max(0, (cta.bottom - 2) - source.bottom)};
    });
    reads.forEach(({pair, opening: isOpening, pillarBox, base, title, cta, hatchPhase}) => {
      if (isOpening) return;
      if (pillarBox) {
        Object.entries(pillarBox).forEach(([name, value]) => setValue(pair.pillars, name, `${value}px`));
      }
      setValue(pair.carousel, '--ak-paper-header-hatch-y', `${-hatchPhase}px`);
      setValue(pair.divider, '--ak-paper-title-extension', `${title}px`);
      setValue(pair.divider, '--ak-paper-cta-extension', `${cta}px`);
      // Only the opening divider uses the accelerated intro. Subsequent
      // dividers keep their distance-based timing, independent of wheel speed.
      const opening = pair.divider.hasAttribute('data-ak-paper-intro');
      setValue(pair.divider, '--ak-paper-title-draw-duration', opening ? '400ms' : `${500 * (base + title) / base}ms`);
      setValue(pair.divider, '--ak-paper-cta-draw-duration', opening ? '400ms' : `${500 * (base + cta) / base}ms`);
    });
  }
  function queue() {
    // The entrance changes spacer height every frame. Re-measuring all seven
    // headers and rewriting their geometry during that animation causes
    // repeated forced layouts; their line extensions are only needed after it.
    if (pairs.some(pair => pair.introAnimating)) return;
    if (!pending) pending = requestAnimationFrame(sync);
  }
  const sizes = new ResizeObserver(queue);
  const passed = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const pair = pairs.find(item => item.carousel === entry.target);
      if (!pair) return;
      if (pair.divider.hasAttribute('data-ak-paper-intro') && !pair.introSettled) return;
      // Fast jumps and restored scroll positions must never leave blank labels.
      if (pair.divider.getBoundingClientRect().bottom <= 0) {
        reveal(pair, 'title'); reveal(pair, 'cta');
      }
    });
  });
  function init() {
    if (!desktop.matches) return;
    document.querySelectorAll('section.ak-featured-carousel, section[data-ak-collection-editorial]').forEach(carousel => {
      if (pairs.some(pair => pair.carousel === carousel)) return;
      let owner = carousel.closest('.shopify-section');
      let previous = owner?.previousElementSibling;
      const between = [];
      let divider;
      while (previous) {
        divider = previous.querySelector('.ak-vpcompress22-desktop');
        if (divider) break;
        if (previous.querySelector('section.ak-featured-carousel, section[data-ak-collection-editorial]')) break;
        between.push(previous);
        previous = previous.previousElementSibling;
      }
      if (!divider) {
        carousel.classList.add('ak-paper-title-ready', 'ak-paper-cta-ready');
        return;
      }
      // Every page's opening divider uses the same construction entrance.
      if (!pairs.length && !divider.hasAttribute('data-ak-paper-intro')) {
        divider.dataset.akPaperIntro = 'pending';
      }
      const pair = {carousel, divider};
      pairs.push(pair);
      carousel.setAttribute('data-ak-paper-flow', '');
      carousel.classList.add('is-ak-featured-initial-ready');
      divider.setAttribute('data-ak-paper-divider', '');
      divider.closest('.shopify-section').setAttribute('data-ak-paper-divider-owner', '');
      const left = divider.querySelector('.ak-vpcompress22-desktop__line-left');
      const right = divider.querySelector('.ak-vpcompress22-desktop__line-right');
      [[left,'title'],[right,'cta']].forEach(([line, part]) => {
        line.addEventListener('animationend', event => {
          if (event.target === line && event.animationName === 'akVpcompress22DesktopLineDraw') reveal(pair, part);
        });
        if ((!window.AK_BOOT || window.AK_BOOT.revealed) && line.getAnimations().some(animation => animation.playState === 'finished')) reveal(pair, part);
      });
      // Commit each completed stroke once. Keeping a finished CSS animation
      // attached lets later divider-height/duration updates rewind its progress.
      // The persistent stroke is independent of all subsequent door cycles.
      if (document.documentElement.classList.contains('ak-papers-front-page')) {
        divider.querySelectorAll('.ak-vpcompress22-desktop__line').forEach(line => {
          const commit = () => { line.dataset.akPaperLineDrawn = 'true'; };
          line.addEventListener('animationend', event => {
            if (event.target === line && event.animationName === 'akVpcompress22DesktopLineDraw') commit();
          });
          if (line.getAnimations().some(animation => animation.playState === 'finished')) commit();
        });
      }
      sizes.observe(divider);
      sizes.observe(carousel.querySelector('.ak-featured-header'));
      between.forEach(element => sizes.observe(element));
      passed.observe(carousel);
      if (divider.hasAttribute('data-ak-paper-intro')) {
        const main = document.querySelector('#MainContent');
        const pillars = document.createElement('div');
        pillars.className = 'ak-paper-continuous-pillars';
        pillars.setAttribute('aria-hidden', 'true');
        ['left', 'right'].forEach(side => {
          const pillar = document.createElement('div');
          pillar.className = `ak-paper-continuous-pillars__${side}`;
          pillars.append(pillar);
        });
        main.prepend(pillars);
        pair.pillars = pillars;
        main.setAttribute('data-ak-paper-continuous-pillars', '');
        carousel.setAttribute('data-ak-paper-continuous-header', '');
        owner.setAttribute('data-ak-paper-continuous-carousel-owner', '');
        if (document.body.classList.contains('ak-global-frame-pending')) {
          window.addEventListener('ak:page-enter', () => startIntro(pair), {once:true});
          window.addEventListener('ak:global-frame-ready', () => startIntro(pair), {once:true});
        } else if (window.AK_BOOT && !window.AK_BOOT.revealed) {
          window.addEventListener('ak:boot-revealed', () => startIntro(pair), {once:true});
        } else {
          startIntro(pair);
        }
      }
    });
    sync();
  }
  init();
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    pairs.forEach(settleRevisit);
    sync();
  });
  if (document.fonts) document.fonts.ready.then(queue);
  desktop.addEventListener('change', () => {
    pairs.forEach(pair => {
      if (pair.pillars) pair.pillars.style.display = desktop.matches ? '' : 'none';
      if (!desktop.matches) settleRevisit(pair);
    });
    init();
  });
  addEventListener('resize', queue, {passive:true});
  addEventListener('pageshow', queue);
  document.addEventListener('shopify:section:load', init);
})();
